<template>
  <div class="p-2 bg-slate-800/40 rounded space-y-2 text-xs">
    <div class="flex items-center gap-2">
      <span class="text-gray-400 shrink-0 w-24">Incoming</span>
      <BuilderInput type="text" class="flex-1 text-sm px-2 py-1" v-model="editorState.previewValue" placeholder="e.g. on, 42, warning" />
    </div>

    <div v-if="binding.transforms.length > 0" class="flex items-center gap-2">
      <span class="text-gray-400 shrink-0 w-24">Transformed</span>
      <span class="text-white truncate">{{ transformedValue }}</span>
    </div>

    <div class="space-y-1">
      <div v-for="(rule, index) in binding.rules" :key="index"
        class="flex items-center gap-2 px-1.5 py-1 rounded"
        :class="index === matchIndex ? 'bg-cyan-900/50 ring-1 ring-cyan-500' : 'opacity-60'">
        <span class="w-16 shrink-0 text-gray-300">
          {{ rule.when ? `${rule.when.operator} ${rule.when.value}` : 'default' }}
        </span>
        <span class="text-gray-500">→</span>
        <span class="text-gray-300 truncate icon-text">{{ composed(rule.result) }}</span>
      </div>
      <div v-if="binding.rules.length === 0" class="text-gray-500 italic">No rules configured yet.</div>
    </div>

    <div class="flex items-center gap-2 pt-1 border-t border-slate-700">
      <span class="text-gray-400 shrink-0 w-24">Result</span>
      <template v-if="interpolatedColor">
        <span class="inline-block w-4 h-4 rounded border border-slate-500 shrink-0"
          :style="{ backgroundColor: interpolatedColor }" />
        <span class="text-white truncate">{{ interpolatedColor }} (interpolated)</span>
      </template>
      <template v-else-if="matched">
        <span v-if="editorMeta?.editor === 'color'"
          class="inline-block w-4 h-4 rounded border border-slate-500 shrink-0"
          :style="{ backgroundColor: matched.rule.result || 'transparent' }" />
        <span class="text-white truncate icon-text">{{ resultDisplay }}</span>
      </template>
      <span v-else class="text-gray-500 italic">No rule matches this value</span>
    </div>
  </div>
</template>

<script setup>
/**
 * BindingPreview.vue
 *
 * Read-only, editor-only live preview for a 'mapping' binding: given a
 * user-typed test value, shows which of binding.rules currently matches
 * (via evaluateMappingRules.js, the shared evaluation utility — no
 * generator, no Jinja2, no Home Assistant involved) and what result it
 * produces. Deliberately a separate component from MappingRuleRow.vue —
 * MappingRuleRow stays responsible only for editing a rule; this
 * component only displays evaluation outcomes.
 *
 * The preview value and interpolation toggle live in BindingEditorState
 * (see BindingEditorState.js) — transient, editor-only UI state, never
 * persisted, shared with MappingBindingEditor.vue via the same
 * editorState instance rather than held as this component's own hidden
 * local state.
 *
 * binding.transforms (an ordered pipeline, see valueTransforms.js) is
 * part of the persisted AutomationBinding model, not editor state — see
 * "Architecture Correction — Move Value Transforms into AutomationBinding".
 * It runs over editorState.previewValue *before* rule evaluation —
 * Incoming → Transformed → Result, per the "Feature 5 — Value Transforms"
 * brief. When `editorState.interpolationEnabled` is on and
 * interpolateColor.js finds it applicable, the Result line shows the
 * interpolated color instead of the flat matched-rule result. The
 * matching-rule highlight above is unaffected — it always reflects plain
 * evaluateMappingRules(), run on the transformed value.
 *
 * Results are shown composed rather than raw (previewMappingResult.js): a
 * String-Builder-composed result can carry an icon character and the
 * binding's own incoming-value Jinja expression, and the preview's job is to
 * show the plate's text, not the template's source. `.icon-text` below only
 * appends the Material Icons face as a last-resort fallback, so an icon
 * character in the middle of a sentence draws as its glyph while the
 * surrounding text keeps the panel font.
 */
import { computed } from 'vue';
import { evaluateMappingRules } from '../../../automation/schema/evaluateMappingRules.js';
import { interpolateColor } from '../../../automation/schema/interpolateColor.js';
import { runTransformPipeline } from '../../../automation/schema/valueTransforms.js';
import { previewMappingResult } from '../../../automation/schema/previewMappingResult.js';
import BuilderInput from './BuilderInput.vue';

const props = defineProps({
  binding:     { type: Object, required: true }, // AutomationBinding — transforms read here, nothing else mutated
  editorMeta:  { type: Object, default: null },  // from getPropertyEditorMeta(), for the result display
  editorState: { type: Object, required: true }, // BindingEditorState — previewValue, interpolationEnabled
  // The binding's incoming-value Jinja expression (buildIncomingValueExpression),
  // so a composed string result that embeds it previews with the test value
  // substituted. Empty when no entity is selected yet.
  valueExpression: { type: String, default: '' },
});

const transformedValue = computed(() => runTransformPipeline(props.binding.transforms, props.editorState.previewValue));

const matched = computed(() => evaluateMappingRules(props.binding.rules, transformedValue.value));
const matchIndex = computed(() => matched.value?.index ?? -1);

const interpolatedColor = computed(() => {
  if (!props.editorState.interpolationEnabled || props.editorMeta?.editor !== 'color') return null;
  return interpolateColor(props.binding.rules, transformedValue.value);
});

/**
 * A rule's result as it will actually read on the plate — incoming-value
 * expression substituted, icon escapes resolved (see previewMappingResult.js).
 * Used for both the per-rule list above and the Result line below, so the two
 * can never disagree about what a result means.
 */
function composed(result) {
  return previewMappingResult({
    result,
    valueExpression: props.valueExpression,
    value: transformedValue.value,
  });
}

const resultDisplay = computed(() => {
  const result = matched.value?.rule.result ?? '';
  if (props.editorMeta?.editor === 'bool') {
    return (result === true || result === '1' || result === 1 || result === 'true') ? 'On' : 'Off';
  }
  const display = composed(result);
  return display === '' ? '(empty)' : display;
});
</script>

<style scoped>
/* Tailwind's default sans stack with the icon face appended: ordinary
   characters resolve in the UI font, Private Use Area icon characters fall
   through to "Material Icons" (loaded globally — see main.js / assets/
   material-icons.css). */
.icon-text {
  font-family: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, sans-serif, 'Material Icons';
}
</style>
